import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import sponsors from '../images/GRID_01.png';
import AppLayout from '../components/ui/AppLayout';
import { useAuth } from '../AuthContext';
import tokens from '../theme/tokens';
import Squad from './squads/Squad';

const highlights = [
  { icon: <CalendarMonthRoundedIcon />, title: 'Pelada semanal', text: 'Confirmação de presença, ordem de chegada e sorteio equilibrado dos times.' },
  { icon: <EmojiEventsRoundedIcon />, title: 'Torneio anual', text: 'Votação dos potes, grupos, artilharia e eleição do melhor jogador.' },
  { icon: <GroupsRoundedIcon />, title: 'Comunidade', text: 'Seis anos reunindo os moradores do Riviera Boa Viagem em torno do futebol.' },
];

const Hero = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <Box
      sx={{
        color: '#fff',
        background: `radial-gradient(circle at 80% 20%, rgba(215,38,61,0.35), transparent 45%), linear-gradient(135deg, ${tokens.color.ink} 0%, ${tokens.color.inkSoft} 100%)`,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="overline" sx={{ color: tokens.color.brandLight, fontWeight: 700, letterSpacing: '0.12em' }}>
              Riviera Boa Viagem · desde 2019
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.8rem', md: '4.2rem' }, lineHeight: 1, my: 2 }}>
              A pelada do condomínio, levada a sério.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '1.1rem', maxWidth: 560 }}>
              Presença, times, torneio e financeiro da Pelada Riviera em um só lugar.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
              <Button size="large" variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(isAuthenticated ? '/painel' : '/login')}>
                {isAuthenticated ? 'Ir para o painel' : 'Entrar na pelada'}
              </Button>
              <Button size="large" variant="outlined" onClick={() => navigate('/squad')} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                Ver o torneio
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: { xs: 220, md: 320 }, aspectRatio: '1', borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 0 0 12px rgba(255,255,255,0.06)' }}>
              <Box component="img" src="/logo_rfc.png" alt="Riviera FC" sx={{ width: '82%' }} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const Home = () => (
  <AppLayout hero={<Hero />}>
    <Grid container spacing={2} sx={{ mb: 6 }}>
      {highlights.map((item) => (
        <Grid item xs={12} md={4} key={item.title}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 3, bgcolor: tokens.color.brandSoft, color: tokens.color.brand, display: 'grid', placeItems: 'center', mb: 2 }}>
              {item.icon}
            </Box>
            <Typography variant="h6">{item.title}</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>{item.text}</Typography>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
      <Grid item xs={12} md={6}>
        <Typography variant="overline" color="primary" fontWeight={700}>Nossa história</Typography>
        <Typography variant="h4" sx={{ mb: 2 }}>Mais do que um jogo</Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
          A Pelada Riviera, localizada no condomínio Riviera Boa Viagem, é um marco de convivência e esportividade.
          Com seis anos de existência, a tradição nasceu do entusiasmo dos moradores. Todo ano a comunidade se reúne
          no torneio que une os amantes do futebol em momentos de confraternização e diversão. O ambiente amigável e
          competitivo é a oportunidade para peladeiros de todas as idades mostrarem suas habilidades e fortalecerem os
          laços entre vizinhos.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, pb: 1 }}>Patrocinadores</Typography>
          <Box component="img" src={sponsors} alt="Patrocinadores do Riviera FC" sx={{ width: '100%', borderRadius: 2, display: 'block' }} />
        </Card>
      </Grid>
    </Grid>

    <Squad embedded />
  </AppLayout>
);

export default Home;
